<template>
  <div>

    <!-- ── Action Bar ── -->
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap" style="gap: 8px;">
      <h6 class="mb-0 font-weight-bold text-dark">
        Offline Records
        <span v-if="hospital_records && hospital_records.length" class="badge badge-soft-info ml-1">
          {{ hospital_records.length }}
        </span>
      </h6>
      <div class="d-flex" style="gap: 8px;">
        <button data-toggle="modal" data-target="#slideRightModalAll" class="btn btn-outline-secondary btn-sm">
          <i class="fe fe-mail mr-1"></i> View All Codes
        </button>
        <button class="btn btn-submit btn-sm" @click="autoUpdate">
          <i class="fe fe-upload-cloud mr-1"></i> Upload {{ hospital_records && hospital_records.length || 0 }} Records
        </button>
      </div>
    </div>

    <!-- ── Record Cards ── -->
    <div class="row">
      <div class="col-lg-6 mb-4" v-for="record in hospital_records" :key="record.id">
        <div class="card shadow table-card">
          <!-- Card header -->
          <div class="form-card-header">
            <div class="section-number">
              <i class="fe fe-wifi-off" style="font-size: 11px;"></i>
            </div>
            <h6 class="mb-0 font-weight-bold text-white text-truncate">
              {{ record.reasonVisit }} &middot; {{ record.account_type }}
            </h6>
          </div>

          <div class="card-body px-4 py-3">
            <!-- Patient info -->
            <div class="d-flex align-items-center mb-3">
              <div style="width: 46px; height: 46px; background: #eef2ff; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <i class="fe fe-user" style="font-size: 20px; color: #2563eb;"></i>
              </div>
              <div class="ml-3 overflow-hidden">
                <p class="mb-1 font-weight-bold text-truncate" style="font-size: 0.95rem;">
                  {{ record.patient_id.full_name }}
                </p>
                <span class="code-chip">{{ record.patient_id.id_card_number }}</span>
              </div>
            </div>

            <!-- Diagnosis -->
            <div class="info-row mb-3">
              <span class="info-label">
                <i class="fe fe-thermometer info-icon"></i> Diagnosis
              </span>
              <span class="info-value text-truncate" style="max-width: 180px;">
                {{ record.diagnosis.name }}
              </span>
            </div>

            <!-- Stats -->
            <div class="d-flex mb-3" style="gap: 8px;">
              <span class="stat-pill">
                <i class="fe fe-activity"></i> {{ record.services_id.length }} Services
              </span>
              <span class="stat-pill">
                <i class="mdi mdi-pill"></i> {{ record.drug_id.length }} Drugs
              </span>
            </div>

            <!-- Actions -->
            <div class="d-flex" style="gap: 6px;">
              <button class="btn btn-outline-secondary btn-sm flex-grow-1"
                data-toggle="modal" data-target="#slideRightModal"
                @click="getSingleEncounter(record)">
                <i class="fe fe-eye mr-1"></i> View
              </button>
              <button class="btn btn-submit btn-sm flex-grow-1" @click="syncRecord(record)">
                <i class="fe fe-upload-cloud mr-1"></i> Sync
              </button>
              <button class="btn btn-outline-danger btn-sm" @click="deleteRecordSingle(record)" title="Delete">
                <i class="fe fe-trash-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!hospital_records || !hospital_records.length" class="col-12">
        <div class="card shadow table-card text-center py-5">
          <i class="fe fe-inbox" style="font-size: 2.5rem; color: #c7d2e8;"></i>
          <p class="mt-3 mb-0 text-muted">No offline records found</p>
          <small class="text-muted">Records saved offline will appear here</small>
        </div>
      </div>
    </div>

    <!-- ── View Single Encounter Modal ── -->
    <div class="modal fade modal-slide-right" id="slideRightModal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border-bottom: none;">
            <h5 class="modal-title text-white font-weight-bold">
              <i class="fe fe-clipboard mr-2"></i> Encounter Details
            </h5>
            <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body px-4 py-3" v-if="record != ''">
            <!-- Patient card -->
            <div class="p-3 mb-3 rounded" style="background: #f7f9ff; border: 1px solid #e4e9f7;">
              <div class="d-flex align-items-center mb-2">
                <div style="width:40px; height:40px; background:#eef2ff; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0;">
                  <i class="fe fe-user" style="color:#2563eb; font-size:18px;"></i>
                </div>
                <div class="ml-3">
                  <p class="mb-0 font-weight-bold" style="font-size:0.95rem;">{{ record.patient_id.full_name }}</p>
                  <span class="code-chip">{{ record.patient_id.id_card_number }}</span>
                </div>
              </div>
              <div class="info-row">
                <span class="info-label"><i class="fe fe-phone info-icon"></i> Phone</span>
                <span class="info-value">{{ record.patient_id.phone_number }}</span>
              </div>
              <div class="info-row">
                <span class="info-label"><i class="fe fe-calendar info-icon"></i> Date of Visit</span>
                <span class="info-value">{{ record.date_of_visit }}</span>
              </div>
              <div class="info-row">
                <span class="info-label"><i class="fe fe-tag info-icon"></i> Reason</span>
                <span class="info-value">{{ record.reasonVisit }}</span>
              </div>
            </div>

            <!-- Services table -->
            <p class="font-weight-bold mb-2" style="font-size: 0.85rem;">
              <i class="fe fe-activity text-primary mr-1"></i> {{ services_obj.length }} Services
            </p>
            <div class="table-responsive mb-3">
              <table class="table mb-0" style="font-size: 0.82rem;">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Name</th>
                    <th class="table-th">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(service, index) in services_obj" :key="service.id" class="table-row">
                    <td class="px-3 py-2">{{ index + 1 }}</td>
                    <td class="py-2">{{ service.description }}</td>
                    <td class="py-2">{{ service.price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Drugs table -->
            <p class="font-weight-bold mb-2" style="font-size: 0.85rem;">
              <i class="mdi mdi-pill text-primary mr-1"></i> {{ drugs_obj.length }} Drugs
            </p>
            <div class="table-responsive">
              <table class="table mb-0" style="font-size: 0.82rem;">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Name</th>
                    <th class="table-th">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(drug, index) in drugs_obj" :key="drug.id" class="table-row">
                    <td class="px-3 py-2">{{ index + 1 }}</td>
                    <td class="py-2">{{ drug.drug_name }}</td>
                    <td class="py-2">{{ drug.price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-footer" style="border-top: 1px solid #e9ecef;">
            <button type="button" class="btn btn-outline-secondary btn-sm" data-dismiss="modal">Close</button>
            <button type="button" @click="copyText" class="btn btn-submit btn-sm">
              <i class="fe fe-copy mr-1"></i> Copy Code
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── All Short Codes Modal ── -->
    <div class="modal fade modal-slide-right" id="slideRightModalAll" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border-bottom: none;">
            <h5 class="modal-title text-white font-weight-bold">
              <i class="fe fe-mail mr-2"></i> All Encounter Short Codes
            </h5>
            <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body px-4 py-3" id="printDiv" ref="printNow">
            <div class="mb-3 p-3 rounded" style="background:#f7f9ff; border:1px solid #e4e9f7; font-size:0.82rem;"
              v-for="(record, index) in hospital_records" :key="record.id">
              <p class="mb-1 font-weight-bold text-muted" style="font-size:0.75rem;">Record {{ index + 1 }}</p>
              <p class="mb-0" style="font-family: monospace; font-size: 0.8rem; word-break: break-all;">
                EC{{ user.institutional_id }}/{{ record.patient_id.id }}-{{ record.diagnosis.id }}/{{
                  record.date_of_visit | moment("YYYYMMDD")
                }}-{{ record.date_of_admission | moment("YYYYMMDD") }}-{{
                  record.date_of_discharge | moment("YYYYMMDD")
                }}/<span v-for="drug in record.drug_id" :key="drug">{{ drug }},0</span>/<span
                  v-for="service in record.services_id" :key="service">{{ service }},0</span>/{{
                  record.reasonVisit.charAt(0)
                }}{{ user.id }}
              </p>
            </div>
          </div>

          <div class="modal-footer" style="border-top: 1px solid #e9ecef;">
            <button type="button" class="btn btn-outline-secondary btn-sm" data-dismiss="modal">Close</button>
            <button class="btn btn-submit btn-sm" @click="printMe">
              <i class="fe fe-printer mr-1"></i> Print
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import moment from "moment";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Loading },

  data() {
    return {
      isLoading: false,
      fullPage: true,
      user: null,
      records: "",
      record: "",
      drugs_obj: "",
      services_obj: "",
      hospital_records: [],
      hospital_record_index: 0,
    };
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.clients = JSON.parse(localStorage.getItem("clients"));
    this.diseases = JSON.parse(localStorage.getItem("diseases"));
    this.services = JSON.parse(localStorage.getItem("services"));
    this.drugs = JSON.parse(localStorage.getItem("drugs"));
  },

  computed: {
    changeDateofVisit() {
      return moment(String(this.record.date_of_visit)).format("YYYYMMDD");
    },
    changeDateofAdmission() {
      let text = this.record.date_of_admission;
      return text == "0" ? "0" : moment(String(text)).format("YYYYMMDD");
    },
    changeDateofDischarge() {
      let text = this.record.date_of_discharge;
      return text == "0" ? "0" : moment(String(text)).format("YYYYMMDD");
    },
    getSMSCode() {
      let provider = "EC" + this.user.institutional_id + "/";
      let patient = this.record.patient_id.id + "-" + this.record.diagnosis.id + "/";
      let dates = this.changeDateofVisit + "-" + this.changeDateofAdmission + "-" + this.changeDateofDischarge + "/";
      let drugs = this.record.drug_id + "/";
      let services = this.record.services_id + "/";
      let others = this.record.reasonVisit.charAt(0) + this.user.id;
      return provider + patient + dates + drugs + services + others;
    },
  },

  methods: {
    drugsObject(record) {
      let array1 = this.drugs.data;
      let array2 = record.drug_id;
      this.drugs_obj = array1.filter((item) => array2.includes(item.id));
    },

    ServicesObject(record) {
      let array1 = this.services.data;
      let array2 = record.services_id;
      this.services_obj = array1.filter((item) => array2.includes(item.id));
    },

    getSingleEncounter(record) {
      this.isLoading = true;
      let formatter = this.hospital_records.filter((x) => x.id == record.id);
      this.record = formatter[0];
      this.isLoading = false;
      this.drugsObject(record);
      this.ServicesObject(record);
    },

    copyText() {
      navigator.clipboard.writeText(this.getSMSCode);
      this.$toasted.info("Copied to clipboard", { position: "top-center", duration: 3000 });
    },

    async SetDB() {
      this.db = await this.getDb();
      this.hospital_records = await this.getDataFromDb();
      this.ready = true;
    },

    async getDb() {
      const DB_NAME = "hospital_data";
      const DB_VERSION = 1;
      return new Promise((resolve, reject) => {
        let request = window.indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = (e) => { console.log("Error opening db", e); reject("Error"); };
        request.onsuccess = (e) => { resolve(e.target.result); };
        request.onupgradeneeded = (e) => {
          let db = e.target.result;
          db.createObjectStore("hospital_records", { autoIncrement: true, keyPath: "id" });
        };
      });
    },

    async getDataFromDb() {
      return new Promise((resolve) => {
        let trans = this.db.transaction(["hospital_records"], "readonly");
        trans.oncomplete = () => { resolve(hospital_records); };
        let store = trans.objectStore("hospital_records");
        let hospital_records = [];
        store.openCursor().onsuccess = (e) => {
          let cursor = e.target.result;
          if (cursor) { hospital_records.push(cursor.value); cursor.continue(); }
        };
      });
    },

    printMe() {
      let printContents = document.getElementById("printDiv").innerHTML;
      let originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    },

    syncRecord(record) {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .post(`/api/v1/auth/sync-healthrecord`, { request_body: record })
        .then((response) => {
          this.$toasted.success("Synced Successfully", { position: "top-center", duration: 3000 });
          this.deleteRecord(record.id);
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Error on Syncing", { position: "top-center", duration: 3000 });
        });
    },

    autoUpdate() {
      if (confirm("Are you sure you want to upload all data?")) {
        setInterval(() => { this.syncRecordAll(); }, 5000);
      }
    },

    syncRecordAll() {
      if (this.hospital_records.length == 0) {
        this.$router.push("/personnel");
      } else {
        this.axios
          .post(`/api/v1/auth/sync-healthrecord`, { request_body: this.hospital_records[this.hospital_record_index] })
          .then((response) => {
            this.deleteRecord(this.hospital_records[this.hospital_record_index].id);
            this.$toasted.success("Synced Successfully", { position: "top-center", duration: 3000 });
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
            this.$toasted.error("Error on Syncing", { position: "top-center", duration: 3000 });
          });
      }
    },

    async deleteRecord(id) {
      await this.deleteRecordFromDb(id);
      this.hospital_records = await this.getDataFromDb();
    },

    async deleteRecordFromDb(id) {
      return new Promise((resolve) => {
        let trans = this.db.transaction(["hospital_records"], "readwrite");
        trans.oncomplete = () => { resolve(); };
        let store = trans.objectStore("hospital_records");
        store.delete(id);
      });
    },

    deleteRecordSingle(record) {
      if (confirm("Are you sure you want to delete this record?")) {
        this.deleteRecord(record.id);
      }
    },
  },

  created() {
    this.SetDB();
  },
};
</script>

<style scoped>
.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #f0f4ff;
  border: 1px solid #d0d9ee;
  border-radius: 20px;
  font-size: 0.78rem;
  color: #374151;
}
.stat-pill i {
  color: #2563eb;
  font-size: 0.8rem;
}
</style>
